import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Badge, Button, Progress, Avatar, ActionMenu } from '@/components/ui';
import { 
  Folder, FolderOpen, ArrowLeft, Edit2, Trash2, Eye, FileText, Share2, Upload, Plus, Search, Filter, 
  MoreVertical, Star, Activity, Clock, Download, PieChart,
  FileCode, FileSpreadsheet, Image as ImageIcon
} from 'lucide-react';
import { cn } from '@/utils/cn';
import { useAuth } from '@/core/auth/AuthContext';
import { documentsService } from './services/documents.service';
import { ProjectsService } from '@/modules/projects/shared/services/projects.service';
import { toast } from 'sonner';

const DocumentsPage = () => {
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';
  const userId = profile?.id || '';
  const queryClient = useQueryClient();
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('all');

  const { data: projectRes } = useQuery({
    queryKey: ['projects', companyId],
    queryFn: () => ProjectsService.getProjects(companyId),
    enabled: !!companyId,
  });
  const projectsList = projectRes?.data || [];

  const { data: selectedProject } = useQuery({
    queryKey: ['project', selectedProjectId],
    queryFn: () => ProjectsService.getProjectDetail(selectedProjectId),
    enabled: !!selectedProjectId && selectedProjectId !== 'all' && selectedProjectId !== '',
  });

  // Fetch Folders
  const { data: folders = [], isLoading: loadingFolders } = useQuery({
    queryKey: ['documents', 'folders', companyId, currentFolderId, selectedProjectId],
    queryFn: () => documentsService.getFolders(companyId, currentFolderId, selectedProjectId),
    enabled: !!companyId,
  });

  // Fetch Documents
  const { data: documents = [], isLoading: loadingDocs } = useQuery({
    queryKey: ['documents', 'files', companyId, currentFolderId, selectedProjectId],
    queryFn: () => documentsService.getDocuments(companyId, currentFolderId, selectedProjectId),
    enabled: !!companyId,
  });

  // Seed folders mutation
  const seedMutation = useMutation({
    mutationFn: () => documentsService.seedStandardFolders(companyId, userId, selectedProjectId),
    onSuccess: () => {
      toast.success('Đã khởi tạo thư mục chuẩn thành công');
      queryClient.invalidateQueries({ queryKey: ['documents'] });
    },
    onError: (err: any) => toast.error('Lỗi khởi tạo: ' + err.message)
  });

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: (files: File[]) => documentsService.uploadDocuments(files, companyId, currentFolderId, userId, selectedProjectId),
    onSuccess: (count) => {
      toast.success(`Đã tải lên thành công ${count} tài liệu`);
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
    onError: (err: any) => {
      toast.error('Lỗi tải tài liệu: ' + err.message);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      uploadMutation.mutate(Array.from(e.target.files));
    }
  };

  // Mock Data for Dashboard Stats
  const stats = [
    { label: 'Tổng số thư mục', value: folders.length, sub: 'thư mục', icon: Folder, color: 'text-blue-600', bg: 'bg-blue-50' },
    { label: 'Tổng số tài liệu', value: documents.length, sub: 'tài liệu', icon: FileText, color: 'text-emerald-600', bg: 'bg-emerald-50' },
    { label: 'Tổng dung lượng', value: '42.6', sub: 'GB', icon: PieChart, color: 'text-purple-600', bg: 'bg-purple-50' },
    { label: 'Đã chia sẻ', value: '0', sub: 'tài liệu', icon: Share2, color: 'text-amber-600', bg: 'bg-amber-50' },
  ];

  return (
    <div className="p-6 space-y-6 bg-slate-50/30 min-h-screen">
      {/* Header Area */}
      <div className="mb-6">
        <h1 className="text-2xl text-slate-800 tracking-tight leading-none font-normal">Hồ sơ công trình</h1>
        <p className="sm:text-sm text-xs text-slate-400 mt-1">Quản lý, lưu trữ và chia sẻ hồ sơ, tài liệu của công trình.</p>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
        
        {/* Left Column: Stats & Main List */}
        <div className="xl:col-span-3 space-y-6">
          
          {/* Project Summary Card */}
          {selectedProject ? (
            <Card className="p-5 border-none shadow-sm bg-white overflow-hidden relative">
              <div className="absolute top-0 right-0 p-8 bg-emerald-50 rounded-bl-full -mr-4 -mt-4 opacity-50" />
              <div className="relative flex flex-col md:flex-row gap-6">
                <div className="w-full md:w-48 h-32 rounded-lg bg-slate-200 overflow-hidden shadow-inner shrink-0">
                  <img 
                    src={selectedProject.thumbnail_url || "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=500&auto=format&fit=crop"} 
                    alt="Project" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1 space-y-3">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-normal text-slate-800">{selectedProject.name}</h2>
                    <Badge variant={selectedProject.status_code === 'completed' ? 'success' : 'secondary'} className="whitespace-nowrap">
                      {selectedProject.status_code === 'active' || selectedProject.status_code === 'construction' ? 'Đang thi công' : selectedProject.status_code}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-8 sm:text-sm text-xs text-slate-500">
                    <div className="flex items-center gap-2">
                      <span className="font-normal text-slate-400 w-20">Chủ đầu tư:</span>
                      <span className="text-slate-700 truncate">{selectedProject.client_name || 'Đang cập nhật'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-normal text-slate-400 w-20">Ngày bắt đầu:</span>
                      <span className="text-slate-700">{selectedProject.start_date ? new Date(selectedProject.start_date).toLocaleDateString('vi-VN') : 'Đang cập nhật'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-normal text-slate-400 w-20">Địa chỉ:</span>
                      <span className="text-slate-700 truncate" title={selectedProject.location || ''}>{selectedProject.location || 'Đang cập nhật'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-normal text-slate-400 w-20">Dự kiến HT:</span>
                      <span className="text-slate-700">{selectedProject.end_date ? new Date(selectedProject.end_date).toLocaleDateString('vi-VN') : 'Đang cập nhật'}</span>
                    </div>
                  </div>
                  <a href={`/projects/${selectedProject.id}`} className="inline-block sm:text-sm text-xs font-normal text-emerald-600 hover:underline">
                    Xem chi tiết công trình →
                  </a>
                </div>
              </div>
            </Card>
          ) : (
            <Card className="p-5 border-none shadow-sm bg-gradient-to-r from-emerald-700 to-emerald-600 text-white overflow-hidden relative">
              <div className="absolute top-0 right-0 p-12 bg-white rounded-bl-full -mr-8 -mt-8 opacity-10" />
              <div className="relative flex flex-col md:flex-row gap-6 items-center">
                <div className="flex-1 space-y-2">
                  <h2 className="text-xl font-normal tracking-tight">
                    {selectedProjectId === '' ? 'Kho tài liệu Dùng chung' : 'Tổng quan Hồ sơ Hệ thống'}
                  </h2>
                  <p className="sm:text-sm text-xs text-emerald-100/90">
                    {selectedProjectId === '' 
                      ? 'Đang xem và quản lý các tài liệu chung của Công ty (không thuộc công trình cụ thể nào).'
                      : 'Đang xem toàn bộ tài liệu và thư mục thuộc quyền truy cập của bạn trên toàn hệ thống.'}
                  </p>
                </div>
              </div>
            </Card>
          )}

          {/* Quick Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {stats.map((stat, idx) => (
              <Card key={idx} className="p-4 border-none shadow-sm bg-white hover:ring-1 hover:ring-emerald-200 transition-all cursor-pointer group">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="sm:text-sm text-xs font-normal text-slate-400  tracking-tight">{stat.label}</p>
                    <div className="flex items-baseline gap-1 mt-1">
                      <span className="text-2xl font-normal text-slate-800">{stat.value}</span>
                      <span className="sm:text-sm text-xs text-slate-500">{stat.sub}</span>
                    </div>
                  </div>
                  <div className={cn("p-2 rounded-lg", stat.bg)}>
                    <stat.icon className={cn("w-5 h-5", stat.color)} />
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {/* Toolbar Section */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mt-6">
            <div className="flex items-center gap-6 overflow-x-auto no-scrollbar">
              {['Tất cả', 'Thi công', 'Bảo dưỡng'].map((tab, idx) => (
                <button key={idx} className={cn(
                  "sm:text-sm text-xs font-normal whitespace-nowrap pb-1 transition-all relative",
                  idx === 0 ? "text-emerald-700" : "text-slate-400 hover:text-slate-600"
                )}>
                  {tab}
                  {idx === 0 && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-emerald-600 rounded-full" />}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2">
              <select 
                className="w-full md:w-48 px-3 py-2 bg-white border border-slate-200 rounded-md sm:text-sm text-xs text-slate-600 outline-none focus:border-emerald-500 shadow-sm"
                value={selectedProjectId}
                onChange={(e) => setSelectedProjectId(e.target.value)}
              >
                <option value="all">Tất cả hồ sơ</option>
                <option value="">Hồ sơ Dùng chung (Công ty)</option>
                <optgroup label="Hồ sơ theo dự án">
                  {projectsList.map((p: any) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </optgroup>
              </select>
              {!currentFolderId && folders.length === 0 && (
                <Button 
                  variant="outline" 
                  size="md" 
                  className="bg-white border-amber-200 text-amber-700 hover:bg-amber-50"
                  onClick={() => seedMutation.mutate()}
                  disabled={seedMutation.isPending}
                >
                  <Star className="w-4 h-4 mr-2" /> Khởi tạo chuẩn
                </Button>
              )}
              {currentFolderId && (
                <Button variant="outline" size="md" className="bg-white" onClick={() => setCurrentFolderId(null)}>
                  <ArrowLeft className="w-4 h-4 mr-2" /> Quay lại
                </Button>
              )}
              <Button variant="outline" size="md" className="bg-white">
                <Plus className="w-4 h-4 mr-2" /> Thêm thư mục
              </Button>
              
              <input 
                type="file" 
                multiple 
                className="hidden" 
                ref={fileInputRef} 
                onChange={handleFileChange} 
              />
              <Button 
                variant="primary" 
                size="md" 
                className="bg-emerald-700 hover:bg-emerald-800"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadMutation.isPending}
              >
                {uploadMutation.isPending ? (
                  <><i className="ri-loader-4-line mr-2 animate-spin" /> Đang tải...</>
                ) : (
                  <><Upload className="w-4 h-4 mr-2" /> Tải tài liệu</>
                )}
              </Button>
            </div>
          </div>

          {/* Search & Filter Layout */}
          <div className="grid grid-cols-[1fr_auto] gap-2 lg:grid-cols-4 lg:gap-3 mt-4 mb-2">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input type="text" placeholder="Tìm kiếm tài liệu..." className="w-full pl-9 pr-3 py-2 bg-white border border-slate-200 rounded-md sm:text-sm text-xs outline-none focus:border-emerald-500 transition-colors" />
            </div>
            <select className="hidden lg:block w-full px-3 py-2 bg-white border border-slate-200 rounded-md sm:text-sm text-xs text-slate-600 outline-none focus:border-emerald-500">
              <option value="">Tất cả định dạng</option>
              <option value="pdf">PDF</option>
              <option value="doc">Word/Excel</option>
              <option value="img">Hình ảnh</option>
            </select>
            <select className="hidden lg:block w-full px-3 py-2 bg-white border border-slate-200 rounded-md sm:text-sm text-xs text-slate-600 outline-none focus:border-emerald-500">
              <option value="">Người tạo</option>
              <option value="me">Của tôi</option>
            </select>
            <select className="hidden lg:block w-full px-3 py-2 bg-white border border-slate-200 rounded-md sm:text-sm text-xs text-slate-600 outline-none focus:border-emerald-500">
              <option value="">Thời gian</option>
              <option value="newest">Mới nhất</option>
            </select>
            <Button variant="outline" className="lg:hidden h-full px-3 bg-white border-slate-200 text-slate-600">
              <Filter size={14} className="mr-2" /> Bộ lọc
            </Button>
          </div>

          {/* Files Browser */}
          <Card className="border-none shadow-sm bg-white overflow-hidden">

            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50/50 sm:text-sm text-xs  text-slate-400 font-normal tracking-wider">
                    <th className="px-5 py-3 text-center">Tên</th>
                    <th className="px-5 py-3 text-center">Loại</th>
                    <th className="px-5 py-3 text-center">Người tạo</th>
                    <th className="px-5 py-3 text-center">Cập nhật</th>
                    <th className="px-5 py-3 text-center">Thao tác</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {folders.map((folder) => (
                    <tr key={folder.id} className="hover:bg-slate-50/80 transition-colors group cursor-pointer" onClick={() => setCurrentFolderId(folder.id)}>
                      <td className="px-5 py-4 text-left">
                        <div className="flex items-center gap-3">
                          <Folder className="w-5 h-5 text-amber-500" fill="currentColor" fillOpacity={0.2} />
                          <div>
                            <p className="sm:text-sm text-xs font-normal text-slate-700 group-hover:text-emerald-700 transition-colors">{folder.name}</p>
                            <p className="sm:text-sm text-xs text-slate-400">Thư mục</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4 text-left">
                        <Badge variant="secondary" className="whitespace-nowrap font-normal">
                          Folder
                        </Badge>
                      </td>
                      <td className="px-5 py-4 text-left">
                        <span className="sm:text-sm text-xs text-slate-500">Hệ thống</span>
                      </td>
                      <td className="px-5 py-4 sm:text-sm text-xs text-slate-500">{new Date(folder.created_at).toLocaleDateString('vi-VN')}</td>
                      <td className="px-5 py-4 text-left">
                        <ActionMenu items={[
                          { label: 'Mở thư mục', icon: FolderOpen, onClick: () => setCurrentFolderId(folder.id) },
                          { label: 'Đổi tên', icon: Edit2, onClick: () => {} },
                          { label: 'Xóa', icon: Trash2, destructive: true, onClick: () => {} }
                        ]} />
                      </td>
                    </tr>
                  ))}

                  {documents.map((doc) => (
                    <tr key={doc.id} className="hover:bg-slate-50/80 transition-colors group">
                      <td className="px-5 py-4 text-left">
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-blue-500" />
                          <div>
                            <p className="sm:text-sm text-xs font-normal text-slate-700 group-hover:text-emerald-700 transition-colors cursor-pointer">{doc.title}</p>
                            <p className="sm:text-sm text-xs text-slate-400">{Math.round((doc.file_size_bytes || 0) / 1024 / 1024 * 100) / 100} MB</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4 text-left">
                        <Badge variant="secondary" className="whitespace-nowrap font-normal">
                          {doc.document_type || 'FILE'}
                        </Badge>
                      </td>
                      <td className="px-5 py-4 text-left">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center sm:text-sm text-xs font-normal text-slate-500">
                            {doc.user_profile?.full_name?.split(' ').pop()?.[0] || 'U'}
                          </div>
                          <span className="sm:text-sm text-xs text-slate-600">{doc.user_profile?.full_name || 'Unknown'}</span>
                        </div>
                      </td>
                      <td className="px-5 py-4 sm:text-sm text-xs text-slate-500">{new Date(doc.created_at).toLocaleDateString('vi-VN')}</td>
                      <td className="px-5 py-4 text-left">
                        <ActionMenu items={[
                          { label: 'Xem chi tiết', icon: Eye, onClick: () => {} },
                          { label: 'Tải xuống', icon: Download, onClick: () => {} },
                          { label: 'Xóa', icon: Trash2, destructive: true, onClick: () => {} }
                        ]} />
                      </td>
                    </tr>
                  ))}

                  {folders.length === 0 && documents.length === 0 && !loadingFolders && !loadingDocs && (
                    <tr>
                      <td colSpan={5} className="py-12 text-center text-slate-500">
                        <Folder className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                        <p className="sm:text-sm text-xs">Chưa có dữ liệu nào</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile View */}
            <div className="md:hidden divide-y divide-slate-100">
              {folders.map((folder) => (
                <div key={folder.id} className="p-4 bg-white active:bg-slate-50 transition-colors" onClick={() => setCurrentFolderId(folder.id)}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-amber-50 rounded-lg">
                        <Folder className="w-5 h-5 text-amber-500" fill="currentColor" fillOpacity={0.2} />
                      </div>
                      <div>
                        <p className="sm:text-sm text-xs font-normal text-slate-800">{folder.name}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-[10px] sm:text-xs text-slate-400">Thư mục</span>
                          <span className="text-slate-300">•</span>
                          <span className="text-[10px] sm:text-xs text-slate-400">{new Date(folder.created_at).toLocaleDateString('vi-VN')}</span>
                        </div>
                      </div>
                    </div>
                    <ActionMenu items={[
                      { label: 'Mở thư mục', icon: FolderOpen, onClick: () => setCurrentFolderId(folder.id) },
                      { label: 'Đổi tên', icon: Edit2, onClick: () => {} },
                      { label: 'Xóa', icon: Trash2, destructive: true, onClick: () => {} }
                    ]} />
                  </div>
                </div>
              ))}

              {documents.map((doc) => (
                <div key={doc.id} className="p-4 bg-white active:bg-slate-50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-50 rounded-lg">
                        <FileText className="w-5 h-5 text-blue-500" />
                      </div>
                      <div>
                        <p className="sm:text-sm text-xs font-normal text-slate-800">{doc.title}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="secondary" className="px-1.5 py-0 text-[10px] sm:text-xs font-normal">{doc.document_type || 'FILE'}</Badge>
                          <span className="text-slate-300">•</span>
                          <span className="text-[10px] sm:text-xs text-slate-400">{Math.round((doc.file_size_bytes || 0) / 1024 / 1024 * 100) / 100} MB</span>
                        </div>
                      </div>
                    </div>
                    <ActionMenu items={[
                      { label: 'Xem chi tiết', icon: Eye, onClick: () => {} },
                      { label: 'Tải xuống', icon: Download, onClick: () => {} },
                      { label: 'Xóa', icon: Trash2, destructive: true, onClick: () => {} }
                    ]} />
                  </div>
                </div>
              ))}

              {folders.length === 0 && documents.length === 0 && !loadingFolders && !loadingDocs && (
                <div className="py-12 text-center">
                  <Folder className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                  <p className="sm:text-sm text-xs text-slate-500">Chưa có dữ liệu nào</p>
                </div>
              )}
            </div>

            <div className="px-5 py-3 bg-slate-50/50 border-t border-slate-50 flex items-center justify-between">
              <p className="sm:text-sm text-xs text-slate-400 font-normal">Hiển thị 1 - 6 trong tổng số 246 tài liệu</p>
              <div className="flex items-center gap-1">
                <Button size="xs" variant="outline" className="h-7 w-7 p-0 bg-white">1</Button>
                <Button size="xs" variant="ghost" className="h-7 w-7 p-0">2</Button>
                <Button size="xs" variant="ghost" className="h-7 w-7 p-0">3</Button>
              </div>
            </div>
          </Card>
        </div>

        {/* Right Column: Storage & Activities */}
        <div className="space-y-6">
          
          {/* Storage Gauge */}
          <Card className="p-5 border-none shadow-sm bg-white">
            <h3 className="sm:text-sm text-xs font-normal text-slate-800 mb-4">Dung lượng lưu trữ</h3>
            <div className="relative flex flex-col items-center justify-center">
              {/* Simple Circular Gauge Mockup */}
              <div className="w-32 h-32 rounded-full border-[10px] border-slate-50 relative flex items-center justify-center">
                <div className="absolute inset-0 rounded-full border-[10px] border-emerald-600 border-t-transparent border-l-transparent rotate-45" />
                <div className="text-center">
                  <p className="text-lg font-normal text-slate-800">42.6</p>
                  <p className="sm:text-sm text-xs text-slate-400  font-normal tracking-tighter">/ 64 GB</p>
                </div>
              </div>
              <div className="mt-6 w-full space-y-3">
                <div className="flex items-center justify-between sm:text-sm text-xs">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-emerald-600" />
                    <span className="text-slate-500">Đã sử dụng</span>
                  </div>
                  <span className="font-normal text-slate-700">42.6 GB (68%)</span>
                </div>
                <div className="flex items-center justify-between sm:text-sm text-xs">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-slate-200" />
                    <span className="text-slate-500">Còn trống</span>
                  </div>
                  <span className="font-normal text-slate-700">21.4 GB (32%)</span>
                </div>
              </div>
              <Button size="sm" className="w-full mt-6 bg-slate-50 text-slate-600 border-slate-100 hover:bg-slate-100 sm:text-sm text-xs font-normal">
                Quản lý dung lượng
              </Button>
            </div>
          </Card>

          {/* Quick Access */}
          <Card className="p-5 border-none shadow-sm bg-white">
            <h3 className="sm:text-sm text-xs font-normal text-slate-800 mb-4">Truy cập nhanh</h3>
            <div className="space-y-1">
              {[
                { icon: Star, label: 'Tài liệu yêu thích', color: 'text-amber-500' },
                { icon: Share2, label: 'Tài liệu đã chia sẻ', color: 'text-blue-500' },
                { icon: Folder, label: 'Tài liệu được chia sẻ với tôi', color: 'text-emerald-500' },
                { icon: Clock, label: 'Thùng rác', color: 'text-slate-400' },
              ].map((item, idx) => (
                <button key={idx} className="w-full flex items-center justify-between p-2 hover:bg-slate-50 rounded-lg transition-colors group">
                  <div className="flex items-center gap-3">
                    <item.icon size={14} className={item.color} />
                    <span className="sm:text-sm text-xs text-slate-600 group-hover:text-slate-900 transition-colors">{item.label}</span>
                  </div>
                  <MoreVertical size={12} className="text-slate-300" />
                </button>
              ))}
            </div>
          </Card>

          {/* Recent Activity */}
          <Card className="p-5 border-none shadow-sm bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="sm:text-sm text-xs font-normal text-slate-800">Hoạt động gần đây</h3>
              <button className="sm:text-sm text-xs font-normal text-emerald-600 hover:underline">Xem tất cả</button>
            </div>
            <div className="space-y-5">
              {[
                { user: 'AN', action: 'đã tải lên', target: 'Biện pháp thi công phần móng.pdf', time: '15/05/2024 08:20', icon: FileText, color: 'bg-rose-50 text-rose-600' },
                { user: 'NM', action: 'cập nhật', target: 'Bảng tiến độ tổng thể.xlsx', time: '15/05/2024 07:45', icon: FileSpreadsheet, color: 'bg-emerald-50 text-emerald-600' },
                { user: 'TT', action: 'chia sẻ', target: 'Mặt bằng tầng điển hình.dwg', time: '14/05/2024 16:40', icon: FileCode, color: 'bg-blue-50 text-blue-600' },
              ].map((activity, idx) => (
                <div key={idx} className="flex gap-3 relative">
                  {idx !== 2 && <div className="absolute top-8 left-4 bottom-0 w-px bg-slate-100" />}
                  <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center shrink-0", activity.color)}>
                    <activity.icon size={14} />
                  </div>
                  <div className="space-y-1">
                    <p className="sm:text-sm text-xs leading-tight">
                      <span className="font-normal text-slate-800">{activity.user}</span> {activity.action} <span className="font-normal text-emerald-700">{activity.target}</span>
                    </p>
                    <p className="text-[9px] text-slate-400 font-normal flex items-center gap-1">
                      <Clock size={10} /> {activity.time}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </Card>

        </div>
      </div>
    </div>
  );
};

export default DocumentsPage;
